import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { activeAccount, ensureInitialized, msal, signIn } from '../auth/msal'
import { authConfig } from '../auth/config'

export const Route = createFileRoute('/login')({
  beforeLoad: () => ensureInitialized(),
  component: LoginScreen,
})

/**
 * The bespoke Login — canonical per the build plan. Re-expressed from the
 * design reference (screens/jedi-app/Login) as function components against
 * the token files; the redirect phase is real MSAL, not the canvas's timer.
 */
function LoginScreen() {
  const [phase, setPhase] = useState<'idle' | 'redirecting'>('idle')
  const account = activeAccount()
  const navigate = useNavigate()
  const tenantLine = new URL(authConfig.authority).host

  const onSignIn = () => {
    setPhase('redirecting')
    void signIn()
  }

  const onDifferentAccount = () => {
    setPhase('redirecting')
    void msal.loginRedirect({ scopes: [authConfig.apiScope], prompt: 'select_account' })
  }

  return (
    <div className="login">
      <div className="login-brand">
        <div className="login-brand-row">
          <div className="login-mark" />
          <div className="login-wordmark">J.E.D.I.</div>
          <div className="login-vr" />
          <div className="login-org">Intact Technology</div>
        </div>

        <div className="login-hero">
          <p className="login-eyebrow">Judgement Enhanced Delivery Intelligence</p>
          <h1>
            J.E.D.I. proposes.
            <br />
            <span>You decide.</span>
          </h1>
          <p className="login-lede">
            Intact&rsquo;s AI-led delivery platform for outcome-based engagements.
          </p>
        </div>

        <div className="login-foot">
          <span>© 2026 Intact Technology</span>
          <span className="login-foot-vr" />
          <a href="#">Privacy</a>
          <a href="#">Platform status</a>
        </div>
      </div>

      <div className="login-panel">
        <div className="login-card">
          {phase === 'redirecting' ? (
            <div>
              <div className="login-spinner" />
              <h2>Handing you to Microsoft</h2>
              <p className="login-sub">
                Verifying your Intact account with Azure. This takes a moment.
              </p>
              <div className="login-sweep">
                <div />
              </div>
              <div className="login-tenant">{tenantLine}</div>
            </div>
          ) : account ? (
            <div>
              <div className="login-identity">
                <div className="login-avatar">
                  {(account.name ?? account.username)
                    .split(' ')
                    .map((part) => part[0])
                    .slice(0, 2)
                    .join('')}
                </div>
                <div>
                  <div className="login-identity-name">{account.name ?? account.username}</div>
                  <div className="login-identity-upn">{account.username}</div>
                </div>
              </div>
              <h2>You&rsquo;re in</h2>
              <button
                type="button"
                className="login-continue"
                onClick={() => void navigate({ to: '/' })}
              >
                Continue to engagement
              </button>
              <button type="button" className="login-switch" onClick={onDifferentAccount}>
                Use a different account
              </button>
            </div>
          ) : (
            <div>
              <h2>Sign in</h2>
              <p className="login-sub">
                Use your Intact work account. J.E.D.I. only sees the engagements you&rsquo;re on.
              </p>
              <button type="button" className="login-ms" onClick={onSignIn}>
                <svg width="17" height="17" viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="0" y="0" width="9.5" height="9.5" fill="#F25022" />
                  <rect x="11.5" y="0" width="9.5" height="9.5" fill="#7FBA00" />
                  <rect x="0" y="11.5" width="9.5" height="9.5" fill="#00A4EF" />
                  <rect x="11.5" y="11.5" width="9.5" height="9.5" fill="#FFB900" />
                </svg>
                Continue with Microsoft
              </button>
              <div className="login-sso-note">
                <span className="login-dot" />
                Azure single sign-on · no separate password
              </div>
              <div className="login-after">
                <div className="login-after-item">
                  <div className="login-after-bar" />
                  <p>
                    After sign-in you land on your engagement. If you&rsquo;re on more than one,
                    you&rsquo;ll pick from the engagement list.
                  </p>
                </div>
                <div className="login-help">
                  Trouble signing in? <a href="#">Ask your Intact admin</a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
